/**
 * V152 — Merkflow AI chat panel.
 *
 * Slide-over on the right (`w-[640px]` desktop, full-width mobile) with a
 * two-panel layout: narrow conversation history sidebar on the left, the
 * active conversation on the right. Both panels are always visible on
 * desktop; mobile collapses to one-at-a-time with a back button.
 *
 * Cache notes:
 *   - `chat-conversations` (no workspace arg — user-scoped)
 *   - `chat-conversation`, conversationId — full message history
 *   - `chat-limits`, workspaceId — workspace-scoped tier caps + counter
 *
 * The send mutation appends to local state optimistically, then
 * invalidates the three caches on success so the sidebar timestamp, the
 * conversation row, and the usage counter all reflect the new message.
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import { chatbotApi } from '@/lib/api';
import { useWorkspaceContext } from '@/components/WorkspaceProvider';
import { ChatHistorySidebar } from './ChatHistorySidebar';
import { ChatConversationView } from './ChatConversationView';

interface Props {
  open: boolean;
  onClose: () => void;
}

export function ChatPanel({ open, onClose }: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { activeWorkspace } = useWorkspaceContext();
  const workspaceId = activeWorkspace?.id ?? '';

  // Active conversation id. null = unsent new conversation (welcome state).
  const [activeId, setActiveId] = useState<string | null>(null);
  // Mobile-only: which panel is visible. Desktop renders both.
  const [mobileView, setMobileView] = useState<'history' | 'chat'>('chat');

  const conversationsQuery = useQuery({
    queryKey: ['chat-conversations'],
    queryFn: () => chatbotApi.listConversations(),
    enabled: open,
  });
  const conversations = conversationsQuery.data ?? [];

  const activeQuery = useQuery({
    queryKey: ['chat-conversation', activeId],
    queryFn: () => chatbotApi.getConversation(activeId!),
    enabled: open && !!activeId,
  });

  const limitsQuery = useQuery({
    queryKey: ['chat-limits', workspaceId],
    queryFn: () => chatbotApi.getLimits(workspaceId),
    enabled: open && !!workspaceId,
  });

  // ESC closes the panel.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  const startNew = () => {
    setActiveId(null);
    setMobileView('chat');
  };

  const selectConversation = (id: string) => {
    setActiveId(id);
    setMobileView('chat');
  };

  const handleSent = (conversationId: string) => {
    setActiveId(conversationId);
    // Refresh history sidebar + this conversation + usage counter.
    qc.invalidateQueries({ queryKey: ['chat-conversations'] });
    qc.invalidateQueries({ queryKey: ['chat-conversation', conversationId] });
    qc.invalidateQueries({ queryKey: ['chat-limits', workspaceId] });
  };

  const handleDeleted = (deletedId: string) => {
    qc.invalidateQueries({ queryKey: ['chat-conversations'] });
    if (activeId === deletedId) {
      setActiveId(null);
    }
  };

  if (!open) return null;

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.aside
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          exit={{ x: '100%' }}
          transition={{ type: 'spring', damping: 28, stiffness: 280 }}
          className="bg-card border-border absolute top-0 right-0 flex h-full w-full flex-col border-l shadow-2xl md:w-[640px] md:flex-row"
          onClick={(e) => e.stopPropagation()}
        >
          <ChatHistorySidebar
            conversations={conversations}
            activeId={activeId}
            isLoading={conversationsQuery.isLoading}
            mobileView={mobileView}
            usage={limitsQuery.data}
            onNew={startNew}
            onSelect={selectConversation}
            onDeleted={handleDeleted}
          />
          <ChatConversationView
            workspaceId={workspaceId}
            conversation={activeQuery.data}
            isLoading={!!activeId && activeQuery.isLoading}
            activeId={activeId}
            mobileView={mobileView}
            onClose={onClose}
            onBack={() => setMobileView('history')}
            onSent={handleSent}
            promptSuggestions={[
              t('chatbot.welcomePrompt1'),
              t('chatbot.welcomePrompt2'),
              t('chatbot.welcomePrompt3'),
            ]}
          />
        </motion.aside>
      </motion.div>
    </AnimatePresence>,
    document.body,
  );
}
